import { FC, ReactNode } from 'react';
import { Checkbox, Collapse, Typography } from 'antd';
import {
    TbArrowsExchange,
    TbChevronDown,
    TbDatabase,
    TbPlane,
    TbShip,
    TbTrain,
    TbTruck,
    TbWorld,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    EmptySection,
    OverflowFadeScrollArea,
    SearchFilterInput,
    SearchFilterSectionLabel,
    SearchFilterSectionOverflow,
} from '@/shared';
import { useGlobalTradeCatalog } from '@/shared/components/data-visualization/global-trade';

import { TRADE_MODE_OPTIONS, TRADE_REGIONS, TradeMode } from '../../model';
import { GlobalTradeFilterValues } from './filterValues';

interface GlobalTradeFilterFieldsProps {
    filters: GlobalTradeFilterValues;
    onChange: (key: keyof GlobalTradeFilterValues, value: string[]) => void;
    onQueryChange?: (query: string) => void;
    query?: string;
    selectAllSourcesByDefault?: boolean;
    presentation?: 'collapse' | 'editor';
    selectedSectionKey?: string;
}

interface FilterRowProps {
    checked: boolean;
    description?: string;
    icon?: ReactNode;
    label: string;
    onChange: (checked: boolean) => void;
}

const globalTradeModeIcon: Record<TradeMode, ReactNode> = {
    air: <TbPlane size='1rem' />,
    ocean: <TbShip size='1rem' />,
    rail: <TbTrain size='1rem' />,
    road: <TbTruck size='1rem' />,
};

export const GlobalTradeModeIcon: FC<{ mode: TradeMode }> = ({ mode }) => globalTradeModeIcon[mode];

const toggleValue = <T extends string>(values: T[], value: T, checked: boolean) =>
    checked ? Array.from(new Set([...values, value])) : values.filter((currentValue) => currentValue !== value);

export const GlobalTradeFilterRow: FC<FilterRowProps> = ({ checked, description, icon, label, onChange }) => {
    const { token } = useAntDesignTheme();

    return (
        <label
            style={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                gap: '0.5rem',
                minWidth: 0,
                padding: '0.25rem 0',
            }}
        >
            <Checkbox checked={checked} onChange={(event) => onChange(event.target.checked)} />
            {icon ? (
                <span style={{ color: token.colorTextTertiary, display: 'inline-flex', flex: 'none' }}>{icon}</span>
            ) : null}
            <span style={{ minWidth: 0 }}>
                <Typography.Text>{label}</Typography.Text>
                {description ? (
                    <small
                        style={{
                            color: token.colorTextTertiary,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {description}
                    </small>
                ) : null}
            </span>
        </label>
    );
};

const GlobalTradeFilterFields: FC<GlobalTradeFilterFieldsProps> = ({
    filters,
    onChange,
    onQueryChange,
    presentation = 'collapse',
    query,
    selectAllSourcesByDefault = false,
    selectedSectionKey,
}) => {
    const { token } = useAntDesignTheme();
    const tradeCatalog = useGlobalTradeCatalog();
    const selectedSourceIds =
        selectAllSourcesByDefault && filters.sourceIds.length === 0
            ? tradeCatalog.sources.map((source) => source.id)
            : filters.sourceIds;
    const sections = [
        {
            children: tradeCatalog.sources.length ? (
                tradeCatalog.sources.map((source) => (
                    <GlobalTradeFilterRow
                        checked={selectedSourceIds.includes(source.id)}
                        description={source.description}
                        icon={<TbDatabase />}
                        key={source.id}
                        label={source.label}
                        onChange={(checked) => {
                            const nextSourceIds = toggleValue(selectedSourceIds, source.id, checked);
                            onChange(
                                'sourceIds',
                                selectAllSourcesByDefault && nextSourceIds.length === tradeCatalog.sources.length
                                    ? []
                                    : nextSourceIds,
                            );
                        }}
                    />
                ))
            ) : tradeCatalog.loading ? (
                <Typography.Text style={{ color: token.colorTextTertiary }}>Loading data sources…</Typography.Text>
            ) : (
                <EmptySection
                    description='Connected trade datasets will appear here.'
                    height='8rem'
                    icon={<TbDatabase />}
                    label='No Data Sources'
                />
            ),
            icon: <TbDatabase />,
            key: 'sources',
            label: 'Data Sources',
        },
        {
            children: TRADE_MODE_OPTIONS.map((mode) => (
                <GlobalTradeFilterRow
                    checked={filters.modes.includes(mode.value)}
                    icon={<GlobalTradeModeIcon mode={mode.value} />}
                    key={mode.value}
                    label={mode.label}
                    onChange={(checked) => onChange('modes', toggleValue(filters.modes, mode.value, checked))}
                />
            )),
            icon: <TbArrowsExchange />,
            key: 'modes',
            label: 'Transport Modes',
        },
        {
            children: TRADE_REGIONS.map((region) => (
                <GlobalTradeFilterRow
                    checked={filters.regions.includes(region)}
                    key={region}
                    label={region}
                    onChange={(checked) => onChange('regions', toggleValue(filters.regions, region, checked))}
                />
            )),
            icon: <TbWorld />,
            key: 'regions',
            label: 'Regions',
        },
    ];

    if (presentation === 'editor') {
        return <>{sections.find((section) => section.key === selectedSectionKey)?.children}</>;
    }

    return (
        <OverflowFadeScrollArea
            aria-label='Global trade filters'
            containerStyle={{ height: '100%', minHeight: 0 }}
            style={{ paddingRight: '0.25rem' }}
            surface='base'
        >
            <small style={{ color: token.colorTextTertiary }}>Global Trade Filters ({sections.length})</small>
            {onQueryChange ? (
                <div style={{ marginTop: '0.75rem' }}>
                    <SearchFilterInput
                        ariaLabel='Search trade corridors'
                        placeholder='Search Corridors...'
                        setValue={onQueryChange}
                        value={query ?? ''}
                    />
                </div>
            ) : null}
            <Collapse
                expandIcon={({ isActive }) => (
                    <TbChevronDown
                        size='1rem'
                        style={{
                            color: token.colorTextTertiary,
                            transform: `rotate(${isActive ? '0deg' : '-90deg'})`,
                        }}
                    />
                )}
                expandIconPosition='end'
                items={sections.map((section) => ({
                    children: <SearchFilterSectionOverflow>{section.children}</SearchFilterSectionOverflow>,
                    key: section.key,
                    label: <SearchFilterSectionLabel icon={section.icon} label={section.label} />,
                }))}
                size='small'
                style={{ backgroundColor: token.colorBgBase, borderColor: token.colorBorder, margin: '0.75rem 0' }}
            />
        </OverflowFadeScrollArea>
    );
};

export default GlobalTradeFilterFields;
